/**
 * units — single source of truth for imperial ↔ metric conversion and display.
 *
 * Canonical storage:
 *   - weights: lb (pounds) — preserves all existing enferno_ / legacy data
 *   - distance: mi (miles) — preserves existing session.distance entries
 *   - speed:    mph        — derived where needed
 *
 * Display:
 *   - When profile.units === 'metric', convert on read.
 *   - When the user types into an input under metric mode, convert to canonical
 *     before saving.
 *
 * Why store canonical instead of just a number with a unit field?
 *   Backwards compatibility. Every existing logged workout is already in lb / mi
 *   with no unit field. Adding a unit field per-record would require a migration
 *   pass. Keeping canonical means the units toggle is a pure display preference
 *   that can be flipped freely without re-interpreting historical data.
 */

export const LB_PER_KG = 2.2046226218;
export const KM_PER_MI = 1.609344;

// ---------- Labels ----------
export const weightUnit = (units) => (units === 'metric' ? 'kg' : 'lb');
export const distanceUnit = (units) => (units === 'metric' ? 'km' : 'mi');
export const speedUnit = (units) => (units === 'metric' ? 'kph' : 'mph');
export const elevationUnit = (units) => (units === 'metric' ? 'm' : 'ft');
// Pace (time per distance) for run / walk / hike — min per mile vs min per km.
export const paceUnit = (units) => (units === 'metric' ? '/km' : '/mi');

// ---------- Conversions ----------
export const lbToKg = (lb) => (lb || 0) / LB_PER_KG;
export const kgToLb = (kg) => (kg || 0) * LB_PER_KG;
export const miToKm = (mi) => (mi || 0) * KM_PER_MI;
export const kmToMi = (km) => (km || 0) / KM_PER_MI;
export const ftToM = (ft) => (ft || 0) * 0.3048;
export const mToFt = (m) => (m || 0) / 0.3048;
// Pace conversion: a min-per-mile figure covers more ground per unit under km,
// so min/km is the min/mi value divided by (km per mile).
export const minPerMiToMinPerKm = (m) => (m || 0) / KM_PER_MI;

// ---------- Display (lb / mi canonical → user units) ----------

/** Returns a number rounded for display in the user's units. */
export const displayWeight = (lb, units) => {
  if (units !== 'metric') return Math.round(lb || 0);
  // kg: one decimal for sub-100 weights, integer above
  const kg = lbToKg(lb);
  return kg < 100 ? Math.round(kg * 10) / 10 : Math.round(kg);
};

/** Returns a number formatted (toFixed 2) for distance display. */
export const displayDistance = (mi, units) => {
  const v = units === 'metric' ? miToKm(mi || 0) : (mi || 0);
  return v.toFixed(2);
};

/** Returns a number rounded for elevation display. */
export const displayElevation = (ft, units) => {
  if (units !== 'metric') return Math.round(ft || 0);
  return Math.round(ftToM(ft || 0));
};

/** Returns a formatted "X mph" or "Y kph" string for an mph input. */
export const displaySpeed = (mph, units) => {
  const v = units === 'metric' ? mph * KM_PER_MI : mph;
  return `${v.toFixed(1)} ${speedUnit(units)}`;
};

// ---------- Input parsing (user units → canonical) ----------

/** User typed a number in their pref units. Return lb canonical. */
export const parseWeightInput = (value, units) => {
  const n = Number(value);
  if (!isFinite(n) || n <= 0) return 0;
  return units === 'metric' ? kgToLb(n) : n;
};

/** User typed a number in their pref units. Return mi canonical. */
export const parseDistanceInput = (value, units) => {
  const n = Number(value);
  if (!isFinite(n) || n <= 0) return 0;
  return units === 'metric' ? kmToMi(n) : n;
};

// ---------- Steppers ----------

/** Step amount in CANONICAL lb units (so set.weight increments cleanly per user pref). */
export const weightStepLb = (units) => (units === 'metric' ? kgToLb(2.5) : 5);
